import React, { useState } from 'react';
import { useListEmailTemplatesQuery } from '@/redux/email-templates/emailTemplatesApi';
import type { CatalogueEntry } from '@/redux/email-templates/emailTemplatesApi';
import TemplateEditorModal from '@/components/EmailTemplates/TemplateEditorModal';

const EmailTemplatesPage: React.FC = () => {
  const { data, isLoading, isError } = useListEmailTemplatesQuery();
  const [editing, setEditing] = useState<CatalogueEntry | null>(null);

  const templates = (data?.data ?? []).filter((t) => t.key !== 'email_template');

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ui-text">Email Templates</h1>
        <p className="text-ui-text2 text-sm mt-1">
          Manage subject lines and HTML content for all system emails. Changes take effect immediately.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-24 text-ui-text3 text-sm">Loading templates…</div>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-600 text-sm">
          Failed to load email templates.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="bg-ui-card rounded-2xl shadow-sm border border-ui-border2 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ui-border2 bg-ui-raised">
                <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Template</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Subject</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-ui-text2 uppercase tracking-wider">Variables</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ui-border">
              {templates.map((tpl) => {
                const hasTenantOverride = Object.keys(tpl.tenant_value ?? {}).length > 0;
                const subject = String(tpl.effective_value?.subject ?? '');
                const variables: string[] = tpl.variables ?? [];

                return (
                  <tr key={tpl.key} className="hover:bg-ui-hover transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-ui-text">{tpl.name ?? tpl.key}</p>
                        {hasTenantOverride && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                            tenant override
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-ui-text3 mt-0.5">{tpl.description}</p>
                      <code className="text-xs text-ui-text3">{tpl.key}</code>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-ui-text2 max-w-xs truncate">
                        {subject || <span className="text-ui-text3 italic">wrapper template</span>}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {variables.map((v) => (
                          <code key={v} className="bg-brand-light text-brand-dark px-1.5 py-0.5 rounded text-xs">
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setEditing(tpl)}
                        className="text-sm font-medium text-brand-dark hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <TemplateEditorModal entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};

export default EmailTemplatesPage;
